/**
 * Fiskil sync - implements GlossSyncInterface for Fiskil Banking.
 *
 * Flow: generateToken -> createAuthSession -> Link SDK (consent) ->
 * poll sync-status (webhook) -> fetch accounts, balances, transactions -> map and save.
 */

import { inject, Injectable } from "@angular/core";
import { AccountView } from "../../../models/view/account/account.view";
import { GlossSyncInterface } from "../basiq/sync-levels/basiq.sync";
import { FiskilImportData, OriginImportData } from "../gloss.sync";
import {
  fiskilApiIdsMissingFromVault,
  fiskilVaultAccountsMissingImportedTransactions,
  isFiskilImportAlreadyPersisted,
  shouldRecoverFiskilImport,
} from "./fiskil.recovery";
import { GlossSyncService } from "../gloss.sync.service";
import { InstitutionView } from "../../../models/view/institution/institution.view";
import { AppStateStoreService } from "../../store/app-state/app-state.store.service";
import { FiskilApi } from "./fiskil.api";
import { clearFiskilStore } from "./fiskil.store";
import { FiskilAccountAction } from "./actions/fiskil.action.account";
import { FiskilTransactionAction } from "./actions/fiskil.action.transaction";
import { FiskilSourceBalanceAction } from "./actions/fiskil.action.source-balance";
import { SourceAccountView } from "../../../models/view/source-book/source-account.view";
import { isCreditType } from "../../../shared/utils/helper.account/account-type";
import { pollUntil } from "../../../shared/utils/poll-until";
import { Origin, SyncStatusEnum } from "../../../models/types/general-types";
import { UserStoreService } from "../../store/user/user.store.service";
import { ImportStoreService } from "../../store/import/import.store.service";
import { TransactionStoreService } from "../../store/transaction/transaction.store.service";
import { TransactionStatusEnum } from "../../../models/enum/transactionType";
import { CategoryView } from "../../../models/view/category/category.view";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { SourceTransactionsView } from "../../../models/view/source-transactions/source-transactions.view";
import { SourceBalancesView } from "../../../models/view/source-balances/source-balances.view";
import { ConfirmationEnum } from "../../../models/enum/confirmation.enum";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import type { FiskilBalance, FiskilSyncStatusResponse, FiskilTransaction } from "./types";
import { FREE } from "@bitwarden/web-vault/app/models/types/subscription.type";

const BACKGROUND_POLL_INTERVAL_MS = 10_000;
const BACKGROUND_POLL_MAX_DURATION_MS = 30 * 60 * 1000; // 30 min

/** Max active Fiskil consents before blocking new consent flow (matches product / CDR limits). */
const MAX_FISKIL_ACTIVE_CONSENTS = 4;

/**
 * Thrown by getSyncWindowForAccount when the transaction store is not in a known state — null-initialised
 * before tier 2 runs, or nulled by a concurrent clearStore() (logout / purge-vault). A null live set
 * means "unknown", NOT "empty": the account must be SKIPPED this sync, not synced against an empty
 * window. Returning an empty `{ fromDate: undefined, existingIds }` would not skip — the callers act on
 * it, fetching the account's FULL history (no lower bound) and filtering nothing (empty existingIds),
 * re-importing every transaction as a duplicate cipher + source row (#5384). Throwing instead routes
 * through each caller's existing terminateAccountSyncFailure catch, so the account aborts and retries
 * next sync (its `succeeded` flag stays false → never marked synced).
 */
export class SyncWindowUnavailableError extends Error {
  constructor(
    readonly accountId: string | undefined,
    reason: string,
  ) {
    super(`Fiskil sync window unavailable for account ${accountId ?? "?"}: ${reason}`);
    this.name = "SyncWindowUnavailableError";
  }
}

@Injectable({
  providedIn: "root",
})
export class FiskilSync implements GlossSyncInterface {
  private fiskilApi = inject(FiskilApi);
  private glossSyncService = inject(GlossSyncService);
  private appState = inject(AppStateStoreService);
  private fiskilAccountAction = inject(FiskilAccountAction);
  private fiskilTransactionAction = inject(FiskilTransactionAction);
  private fiskilSourceBalanceAction = inject(FiskilSourceBalanceAction);
  private userStore = inject(UserStoreService);
  private importStore = inject(ImportStoreService);
  private transactionStore = inject(TransactionStoreService);
  private logService = inject(LogService);

  private backgroundPollIntervalId: ReturnType<typeof setInterval> | null = null;
  private backgroundPollStartTime: number | null = null;
  private importInFlight: Promise<void> | null = null;

  async connect(isFromGuide: boolean = false): Promise<void> {
    if (!this.ensurePaidTierForFiskil()) {
      return;
    }
    try {
      this.appState.startProcess("fiskil-syncing-data", "Connecting to Fiskil...");

      const { consents = [] } = await this.fiskilApi.getActiveConsents();
      if (consents.length >= MAX_FISKIL_ACTIVE_CONSENTS) {
        if (this.appState.activeProcesses.has("fiskil-syncing-data")) {
          this.appState.endProcess("fiskil-syncing-data");
        }
        this.glossSyncService.confirmationDialog.notify(
          "warning",
          "important",
          "fiskilMaxActiveConsentsReached",
        );
        return;
      }

      await this.fiskilApi.generateToken();
      const authSession = await this.fiskilApi.createAuthSession(isFromGuide);
      if (this.appState.activeProcesses.has("fiskil-syncing-data")) {
        this.appState.endProcess("fiskil-syncing-data");
      }
      window.location.href = authSession.auth_url;
    } catch (error) {
      if (this.appState.activeProcesses.has("fiskil-syncing-data")) {
        this.appState.endProcess("fiskil-syncing-data");
      }
      clearFiskilStore();
      await this.glossSyncService.logError([], error, "Connecting to Fiskil", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /**
   * Redirect flow: Poll 1 min for all three flags; if ready, fetch and save.
   * If not ready after 1 min: end process, toast, start background polling.
   */
  async fetchDataAfterConsent(): Promise<void> {
    if (!this.ensurePaidTierForFiskil()) {
      this.glossSyncService.initializedRunningSyncState();
      return;
    }
    const maxRetries = 12;
    const delayMs = 5000;

    try {
      this.appState.startProcess("fiskil-syncing-data", "Waiting for Fiskil data...");
      const ready = await this.pollUntilAllReady(maxRetries, delayMs, "Fiskil data");

      if (ready) {
        await this.doFetchAndReportImport();
        if (this.appState.activeProcesses.has("fiskil-syncing-data")) {
          this.appState.endProcess("fiskil-syncing-data");
        }
      } else {
        this.appState.endProcess("fiskil-syncing-data");
        this.glossSyncService.confirmationDialog.notify("info", "", "fiskilDataReadyWeWillNotify");
        this.startBackgroundFiskilPolling();
      }
    } catch (error) {
      if (this.appState.activeProcesses.has("fiskil-syncing-data")) {
        this.appState.endProcess("fiskil-syncing-data");
      }
      if (this.appState.activeProcesses.has("importing-data")) {
        this.appState.endProcess("importing-data");
      }
      const err = error instanceof Error ? error : new Error(String(error));
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError([], err, "Fetching Fiskil data", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /** Poll until accounts_synced && balances_synced && transactions_synced. Returns true if ready, false if timed out. */
  private async pollUntilAllReady(
    maxRetries: number,
    delayMs: number,
    label: string,
  ): Promise<boolean> {
    const result = await pollUntil({
      poll: () => this.fiskilApi.getSyncStatus(),
      isReady: (s) => Boolean(s?.accounts_synced && s?.balances_synced && s?.transactions_synced),
      intervalMs: delayMs,
      maxAttempts: maxRetries,
      sleep: (ms) => this.delay(ms),
    });
    return result.ready;
  }

  /** Fetch accounts+balances, transactions, then report import complete. Coalesces concurrent callers. */
  private async doFetchAndReportImport(options?: {
    recovery?: boolean;
    syncStatus?: FiskilSyncStatusResponse;
  }): Promise<void> {
    if (this.importInFlight) {
      return this.importInFlight;
    }

    this.stopBackgroundFiskilPolling();
    this.importInFlight = this.runDoFetchAndReportImport(options).finally(() => {
      this.importInFlight = null;
    });
    return this.importInFlight;
  }

  private async runDoFetchAndReportImport(options?: {
    recovery?: boolean;
    syncStatus?: FiskilSyncStatusResponse;
  }): Promise<void> {
    try {
      // Recovery may be needed when backend reports complete but vault is missing accounts (e.g. user deleted locally).
      if (!options?.recovery && (await this.isImportAlreadyPersistedOnBackend())) {
        return;
      }
      if (options?.recovery && (await this.isRecoveryImportUnneeded(options.syncStatus))) {
        return;
      }
      const { newAccounts, fiskilBalances } = await this.fetchAndSaveAccountsBalances();
      const accountsForTransactions = await this.accountsForTransactionImport(
        newAccounts,
        options?.recovery === true,
      );
      if (accountsForTransactions.length === 0) {
        return;
      }
      if (!this.glossSyncService.isSilentBootstrap()) {
        await this.glossSyncService.markAsSyncing(accountsForTransactions);
      }
      await this.fetchAndSaveTransactions(accountsForTransactions, fiskilBalances);
      await this.reportImportComplete();
      this.stopBackgroundFiskilPolling();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError([], err, "Fiskil doFetchAndReportImport", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  private async reportImportComplete(): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await this.fiskilApi.reportImportComplete();
        return;
      } catch (error) {
        if (attempt === 0) {
          await this.delay(500);
          continue;
        }
        this.logService.error(`Fiskil reportImportComplete failed: ${error}`);
      }
    }
  }

  /** Background poll every 10s; when ready, show confirmation dialog. Stops after 30 min. */
  private startBackgroundFiskilPolling(): void {
    if (this.backgroundPollIntervalId != null) {
      return;
    }
    this.backgroundPollStartTime = Date.now();

    this.backgroundPollIntervalId = setInterval(async () => {
      if (
        this.backgroundPollStartTime != null &&
        Date.now() - this.backgroundPollStartTime > BACKGROUND_POLL_MAX_DURATION_MS
      ) {
        this.stopBackgroundFiskilPolling();
        return;
      }

      try {
        const status = await this.fiskilApi.getSyncStatus();
        if (status?.accounts_synced && status?.balances_synced && status?.transactions_synced) {
          this.stopBackgroundFiskilPolling();
          await this.showFiskilDataReadyConfirmation();
        }
      } catch {
        // ignore
      }
    }, BACKGROUND_POLL_INTERVAL_MS);
  }

  private stopBackgroundFiskilPolling(): void {
    if (this.backgroundPollIntervalId != null) {
      clearInterval(this.backgroundPollIntervalId);
      this.backgroundPollIntervalId = null;
    }
    this.backgroundPollStartTime = null;
  }

  private async showFiskilDataReadyConfirmation(): Promise<void> {
    if (this.importInFlight) {
      return;
    }
    try {
      if (await this.isImportAlreadyPersistedOnBackend()) {
        this.stopBackgroundFiskilPolling();
        return;
      }
    } catch {
      // continue to confirmation dialog
    }

    const confirmed = await this.glossSyncService.confirmationDialog.confirmFor(
      ConfirmationEnum.fiskilDataReadyPullNow,
    );
    if (!confirmed) {
      return;
    }
    if (!this.ensurePaidTierForFiskil()) {
      return;
    }
    try {
      this.appState.startProcess("fiskil-syncing-data", "Importing Fiskil data...");
      await this.doFetchAndReportImport();
    } finally {
      this.appState.endProcess("fiskil-syncing-data");
    }
  }

  private async fetchAndSaveAccountsBalances(): Promise<{
    newAccounts: AccountView[];
    fiskilBalances: FiskilBalance[];
  }> {
    const [accountsResponse, balancesResponse] = await Promise.all([
      this.fiskilApi.getAccounts(),
      this.fiskilApi.getBalances(),
    ]);

    const fiskilAccounts = accountsResponse?.accounts ?? [];
    const fiskilBalances = balancesResponse?.balances ?? [];

    if (fiskilAccounts.length === 0) {
      return { newAccounts: [], fiskilBalances: [] };
    }

    // Org-owned accounts are excluded from the match candidates: a personal account shared/forked
    // into an org (`ManageAccountsComponent.shareAccountToOrg`) keeps the same provider external
    // id (`basiqAccountId`) on both copies, so an unfiltered store-wide search here could resolve
    // to the org fork instead of the personal original and write this refresh's balance/new
    // transactions onto the org's shared cipher instead.
    // `allAccounts` (unfiltered) is kept separately for `buildFiskilSourceAccountView`'s
    // orphan-reuse check, which asks a different question ("does ANY account claim this source
    // row") — filtering it the same way would make a legitimate org-linked source row look
    // orphaned and eligible for silent reuse by this personal refresh.
    const allAccounts = this.userStore.accounts.accountViews() ?? [];
    const existingAccounts = allAccounts.filter((a) => !a.isOrganizationOwned?.());
    const existingFiskilSourceBooks =
      this.importStore.sourceAccount.sourceBookViews()?.filter((s) => s.origin === Origin.fiskil) ??
      [];

    const running = this.glossSyncService.runningSyncState();
    this.glossSyncService.runningSyncState.set({
      ...running,
      accountsCount: fiskilAccounts.length,
      failedAccountsCount: 0,
      successfulAccountsCount: 0,
    });

    const newAccounts: AccountView[] = [];
    const sourceAccountsToSave: SourceAccountView[] = [];
    const sourceBalancesToSave: SourceBalancesView[] = [];

    // Shared across the loop so an orphan claimed by one account can't be re-claimed
    // by a later same-label account in this same import run (#4181/#4430).
    const claimedOrphanIds = new Set<string>();

    await this.fiskilAccountAction.ensureFiskilInstitutionsInVault(fiskilAccounts);

    for (const fiskilAccount of fiskilAccounts) {
      let accountForError: AccountView | null = null;
      try {
        const existingAccount = existingAccounts.find(
          (a) => a.origin === Origin.fiskil && a.basiqAccountId === fiskilAccount.account_id,
        );
        this.appState.startProcess(
          "importing-data",
          `${existingAccount ? "Updating" : "Creating"} ${fiskilAccount.display_name ?? fiskilAccount.product_name ?? "Account"}`,
        );

        const balanceAmount = this.fiskilAccountAction.getBalanceForAccount(
          fiskilBalances,
          fiskilAccount.account_id,
        );

        let rowAccount: AccountView;
        let rowSource: SourceAccountView;
        let rowBalance: SourceBalancesView | undefined;

        if (existingAccount) {
          const clone = existingAccount.clone();
          clone.c_balance = isCreditType(clone.type)
            ? balanceAmount - (clone.creditLimit ?? 0)
            : balanceAmount;
          accountForError = clone;
          rowAccount = clone;
          rowSource = this.fiskilAccountAction.buildFiskilSourceAccountView(
            fiskilAccount,
            clone,
            existingFiskilSourceBooks,
            existingAccounts,
            claimedOrphanIds,
            allAccounts,
          );
          rowBalance = this.fiskilSourceBalanceAction.toSourceBalanceView(
            clone,
            this.fiskilAccountAction.findFiskilBalanceForAccount(
              fiskilBalances,
              fiskilAccount.account_id,
            ),
          );
        } else {
          const institution =
            await this.fiskilAccountAction.getInstitutionForAccount(fiskilAccount);
          const accountProduct = this.fiskilAccountAction.getAccountProduct(
            fiskilAccount,
            institution,
          );
          const balance = fiskilBalances.find((b) => b.account_id === fiskilAccount.account_id);

          const accountStoreModel = this.fiskilAccountAction.toAccountStoreModel(
            fiskilAccount,
            institution,
            accountProduct,
            balance,
          );
          const accountView = new AccountView(accountStoreModel);
          accountView.c_balance = isCreditType(accountView.type)
            ? balanceAmount - (accountView.creditLimit ?? 0)
            : balanceAmount;
          accountForError = accountView;
          rowAccount = accountView;
          rowSource = this.fiskilAccountAction.buildFiskilSourceAccountView(
            fiskilAccount,
            accountView,
            existingFiskilSourceBooks,
            existingAccounts,
            claimedOrphanIds,
            allAccounts,
          );
          rowBalance = this.fiskilSourceBalanceAction.toSourceBalanceView(
            accountView,
            this.fiskilAccountAction.findFiskilBalanceForAccount(
              fiskilBalances,
              fiskilAccount.account_id,
            ),
          );
        }

        newAccounts.push(rowAccount);
        sourceAccountsToSave.push(rowSource);
        if (rowBalance) {
          sourceBalancesToSave.push(rowBalance);
        }
      } catch (error) {
        await this.terminateAccountSyncFailure(
          accountForError,
          error,
          `Fiskil import account (${fiskilAccount.account_id})`,
        );
      } finally {
        this.appState.endProcess("importing-data");
      }
    }

    this.appState.startProcess("importing-data", "Saving accounts...");
    try {
      if (newAccounts.length > 0) {
        await this.userStore.accounts.saveToVault(newAccounts, true);
      }
      if (sourceAccountsToSave.length > 0) {
        await this.importStore.sourceAccount.saveToVault(sourceAccountsToSave, true);
      }
      if (sourceBalancesToSave.length > 0) {
        await this.importStore.sourceBalance.saveToVault(sourceBalancesToSave, true);
      }
    } finally {
      this.appState.endProcess("importing-data");
    }

    const persistedAccounts =
      newAccounts.length > 0 ? this.accountsWithVaultIdsFromStore(newAccounts) : [];

    return { newAccounts: persistedAccounts, fiskilBalances };
  }

  private async fetchAndSaveTransactions(
    newAccounts: AccountView[],
    fiskilBalances: FiskilBalance[],
  ): Promise<void> {
    const accountsWithBalance = newAccounts.map((account) => account.clone());

    for (const account of accountsWithBalance) {
      let succeeded = false;
      try {
        await this.syncAccountTransactions(account);
        // saveCBAT catches its own errors and returns false — treat that as a sync failure so the
        // account isn't marked synced and is retried next sync (#5546). Basiq already checks this.
        const cbatSaved = await this.fiskilTransactionAction.saveCBAT(
          account,
          this.fiskilTransactionAction.getBalanceForAccount(fiskilBalances, account.basiqAccountId),
        );
        if (!cbatSaved) {
          throw new Error("saveCBAT failed");
        }
        succeeded = true;
      } catch (error) {
        await this.terminateAccountSyncFailure(
          account,
          error,
          `Fiskil import transactions (${account.id})`,
        );
      } finally {
        if (this.appState.activeProcesses.has("importing-data")) {
          this.appState.endProcess("importing-data");
        }
        if (succeeded) {
          await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
        }
      }
    }
  }

  private async syncAccountTransactions(account: AccountView): Promise<void> {
    this.appState.startProcess("importing-data", `Pulling transactions for ${account.name}`);

    try {
      const { fromDate, existingIds } = await this.getSyncWindowForAccount(account);
      const transactionsResponse = await this.fiskilApi.getTransactions(
        account.basiqAccountId,
        fromDate,
      );
      const fiskilTransactions = transactionsResponse?.transactions ?? [];
      const newFiskilTransactions = fiskilTransactions.filter(
        (tx) => !existingIds.has(tx.transaction_id),
      );

      if (newFiskilTransactions.length === 0) {
        return;
      }

      const { transactionCategoryMap, categoriesToSave } =
        this.mapFiskilCategoriesToViews(newFiskilTransactions);

      if (categoriesToSave.length > 0) {
        await this.userStore.categories.saveToVault(categoriesToSave, true);
      }

      const { transactionViews, sourceTransactionViews } =
        this.buildFiskilTransactionsWithSourceRows(
          account,
          newFiskilTransactions,
          transactionCategoryMap,
        );

      if (sourceTransactionViews.length > 0) {
        await this.importStore.sourceTransaction.saveToVault(sourceTransactionViews, true);
      }
      await this.transactionStore.transaction.saveToVault(transactionViews, true);
    } finally {
      this.appState.endProcess("importing-data");
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Return accounts from the vault store so each view has vaultId (`vid`) for subsequent saves.
   * Same pattern as Basiq {@link BasiqAccountAction.saveNewAccounts} — in-memory views lack vid after create.
   */
  private accountsWithVaultIdsFromStore(accounts: AccountView[]): AccountView[] {
    const fromStore = this.userStore.accounts.accountViews() ?? [];
    return fromStore.reduce<AccountView[]>((acc, view) => {
      const match = accounts.find((account) => account.id === view.id);
      if (match) {
        const clone = view.clone();
        clone.c_balance = match.c_balance;
        acc.push(clone);
      }
      return acc;
    }, []);
  }

  private async isImportAlreadyPersistedOnBackend(): Promise<boolean> {
    const status = await this.fiskilApi.getSyncStatus();
    return isFiskilImportAlreadyPersisted(status);
  }

  /** Recovery only when vault is behind Fiskil API (or backend still has unsaved data). */
  private async isRecoveryImportUnneeded(
    knownSyncStatus?: FiskilSyncStatusResponse,
  ): Promise<boolean> {
    // A null transaction store is "unknown", not "empty": tier 2 hasn't loaded yet (enrich, which this used
    // to read, stays null for the whole sync while refresh is blocked). A recovery import can't accomplish
    // anything in that state — every getSyncWindowForAccount would throw (#5384) and be swallowed per
    // account, yet reportImportComplete would still fire at the end and clear the backend's
    // has_unsaved_synced_data with nothing saved. Defer across ALL paths below (backend-flag recovery and
    // API reconciliation alike): report "unneeded" so no recovery runs this pass; the next bootstrap with a
    // loaded store re-evaluates and recovers for real. #5532.
    const storeTransactionViews = this.transactionStore.transaction.transactionViews();
    if (storeTransactionViews == null) {
      return true;
    }

    const status = knownSyncStatus ?? (await this.fiskilApi.getSyncStatus());
    if (shouldRecoverFiskilImport(status)) {
      return false;
    }
    if (!isFiskilImportAlreadyPersisted(status)) {
      return false;
    }

    // Read the store, not the enriched collection: enrich drops rows whose account/institution isn't
    // resolved and excludes CBAT/closing rows, so a still-stored imported row could look missing. #5532.
    const vaultFiskilAccounts = this.userStore.getFiskilAccounts();
    if (
      fiskilVaultAccountsMissingImportedTransactions(vaultFiskilAccounts, storeTransactionViews)
    ) {
      return false;
    }

    const apiAccountIds = await this.fiskilApi.listApiAccountIdsWithRetry();
    if (apiAccountIds === null) {
      return false;
    }
    if (apiAccountIds.length === 0) {
      return true;
    }
    return fiskilApiIdsMissingFromVault(apiAccountIds, vaultFiskilAccounts).length === 0;
  }

  /** After account save, import txs for new rows or existing vault rows in recovery mode. */
  private async accountsForTransactionImport(
    newAccounts: AccountView[],
    recovery: boolean,
  ): Promise<AccountView[]> {
    if (newAccounts.length > 0) {
      return newAccounts;
    }
    if (!recovery) {
      return [];
    }
    const existing = this.userStore.accounts
      .accountViews()
      .filter((a) => a.origin === Origin.fiskil);
    return this.accountsWithVaultIdsFromStore(existing);
  }

  /** Per-account failure handling (same pattern as {@link BasiqAccountSync.syncAccount}). */
  private async terminateAccountSyncFailure(
    account: AccountView | null,
    error: unknown,
    level: string,
  ): Promise<void> {
    const err = error instanceof Error ? error : new Error(String(error));
    this.glossSyncService.runningSyncState.set({
      ...this.glossSyncService.runningSyncState(),
      isRunning: false,
      isError: true,
      message: "somethingWentWrong",
    });
    await this.glossSyncService.logError(
      account ? [account] : [],
      err,
      level,
      SyncStatusEnum.unknownError,
    );
    if (account) {
      await this.glossSyncService.terminateSync([account], SyncStatusEnum.unknownError, err);
    }
  }

  /**
   * Fiskil (Open Banking) requires a non-free subscription tier.
   * `activeSubscription` missing is treated as not eligible.
   */
  private isNoFreeSubscriptionTier(): boolean {
    const sub = this.userStore.subscription.activeSubscription();
    return sub != null && sub.tier !== FREE;
  }

  /** @returns true if Fiskil sync can proceed; otherwise shows a toast and returns false */
  private ensurePaidTierForFiskil(): boolean {
    if (this.isNoFreeSubscriptionTier()) {
      return true;
    }
    // Status still resolving: don't block with a "Bundle required" dialog while
    // activeSubscription() is unresolved — it flashes for paid users on load (#14137).
    if (!this.userStore.subscription.isResolved()) {
      return false;
    }
    this.glossSyncService.confirmationDialog.notify(
      "warning",
      "premiumRequired",
      "premiumRequiredDesc",
    );
    return false;
  }

  async connectToInstitution(_institution: InstitutionView): Promise<void> {
    await this.connect();
  }

  async reconnectToInstitution(_institution: InstitutionView): Promise<void> {
    await this.connect();
  }

  async import(originImportData: OriginImportData): Promise<void> {
    try {
      const importData = (originImportData ?? {}) as FiskilImportData;
      if (importData.mode === "recovery") {
        await this.fetchDataForRecovery(importData.syncStatus);
      } else {
        await this.fetchDataAfterConsent();
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.glossSyncService.runningSyncState.set({
        ...this.glossSyncService.runningSyncState(),
        isRunning: false,
        isError: true,
        message: "somethingWentWrong",
      });
      await this.glossSyncService.logError([], err, "Fiskil import", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
    }
  }

  /**
   * Recovery mode: fetch and save without webhook polling (for interrupted sessions).
   */
  private async fetchDataForRecovery(syncStatus?: FiskilSyncStatusResponse): Promise<void> {
    if (!this.ensurePaidTierForFiskil()) {
      this.glossSyncService.initializedRunningSyncState();
      return;
    }
    try {
      this.appState.startProcess("fiskil-syncing-data", "Recovering Fiskil import...");
      await this.doFetchAndReportImport({ recovery: true, syncStatus });
    } finally {
      this.appState.endProcess("fiskil-syncing-data");
    }
  }

  async sync(accounts: AccountView[]): Promise<void> {
    if (accounts.length === 0) {
      return;
    }
    if (!this.ensurePaidTierForFiskil()) {
      await this.glossSyncService.terminateSync(accounts, SyncStatusEnum.unknownError);
      return;
    }
    try {
      await this.syncAccounts(accounts);
    } catch (error) {
      this.appState.endProcess("importing-data");
      await this.glossSyncService.logError(accounts, error, "Syncing Fiskil accounts", null);
      throw error;
    }
  }

  /**
   * For each account: get balance, get transactions (with from_date if we have existing tx),
   * deduplicate, save transactions, save CBAT.
   */
  private async syncAccounts(accounts: AccountView[]): Promise<void> {
    let fiskilBalances: FiskilBalance[];
    try {
      const balancesResponse = await this.fiskilApi.getBalances();
      fiskilBalances = balancesResponse?.balances ?? [];
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      for (const account of accounts) {
        await this.terminateAccountSyncFailure(account, err, "Fiskil getBalances for sync");
      }
      return;
    }

    for (const account of accounts) {
      let succeeded = false;
      try {
        this.appState.startProcess("importing-data", `Syncing ${account.name}`);
        const balance = this.fiskilAccountAction.getBalanceForAccount(
          fiskilBalances,
          account.basiqAccountId,
        );

        const { fromDate, existingIds } = await this.getSyncWindowForAccount(account);
        const transactionsResponse = await this.fiskilApi.getTransactions(
          account.basiqAccountId,
          fromDate,
        );
        const fiskilTransactions = transactionsResponse?.transactions ?? [];

        const newTransactions = fiskilTransactions.filter(
          (tx) => !existingIds.has(tx.transaction_id),
        );

        if (newTransactions.length > 0) {
          const { transactionCategoryMap, categoriesToSave } =
            this.mapFiskilCategoriesToViews(newTransactions);

          if (categoriesToSave.length > 0) {
            await this.userStore.categories.saveToVault(categoriesToSave, true);
          }

          const { transactionViews, sourceTransactionViews } =
            this.buildFiskilTransactionsWithSourceRows(
              account,
              newTransactions,
              transactionCategoryMap,
            );

          if (sourceTransactionViews.length > 0) {
            await this.importStore.sourceTransaction.saveToVault(sourceTransactionViews, true);
          }
          await this.transactionStore.transaction.saveToVault(transactionViews, true);
        }

        const balanceRecord = this.fiskilAccountAction.findFiskilBalanceForAccount(
          fiskilBalances,
          account.basiqAccountId,
        );
        const sourceBalanceView = this.fiskilSourceBalanceAction.toSourceBalanceView(
          account,
          balanceRecord,
        );
        if (sourceBalanceView) {
          await this.importStore.sourceBalance.saveToVault([sourceBalanceView], true);
        }

        // saveCBAT catches its own errors and returns false — treat that as a sync failure so the
        // account isn't marked synced and is retried next sync (#5546).
        const cbatSaved = await this.fiskilTransactionAction.saveCBAT(account, balance);
        if (!cbatSaved) {
          throw new Error("saveCBAT failed");
        }
        succeeded = true;
      } catch (error) {
        await this.terminateAccountSyncFailure(
          account,
          error,
          `Syncing Fiskil account (${account.id})`,
        );
      } finally {
        this.appState.endProcess("importing-data");
        if (succeeded) {
          await this.glossSyncService.terminateSync([account], SyncStatusEnum.synced);
        }
      }
    }
  }

  /**
   * Builds a safe sync window:
   * - removes local pending transactions (and their source rows) so provider state can replace them
   * - returns `fromDate` = earliest pending date (if any) else latest non-pending date
   * - returns `existingIds` from the non-pending snapshot to avoid stale post-delete reads
   */
  private async getSyncWindowForAccount(account: AccountView): Promise<{
    fromDate: string | undefined;
    existingIds: Set<string>;
  }> {
    // Skip the account if the transaction store isn't in a known state — null-initialised before tier 2
    // runs, or nulled by a concurrent clearStore() (logout / purge-vault). A null live set is "unknown",
    // not "empty": throwing (vs returning an empty window the callers act on) aborts + retries next sync
    // via terminateAccountSyncFailure instead of re-importing the full history as duplicates (#5384).
    // This guards the store's state on entry; a clearStore() that lands later, during the pending
    // deleteFromVault await below, is the purge-during-sync race tracked separately in #5531 — a null
    // re-check here can't close it (the stores are re-initialised non-null right after the purge), so the
    // fix belongs at the purge/logout layer, not here.
    const storeTransactionViews = this.transactionStore.transaction.transactionViews();
    if (storeTransactionViews == null) {
      throw new SyncWindowUnavailableError(account.id, "transaction store not initialised");
    }

    const storeAccountTransactions = storeTransactionViews.filter(
      (t) => t.accountLink?.id === account.id,
    );

    // Delete this account's FISKIL-originated pending rows — the provider will re-send those. A manually
    // entered or CSV-imported (origin "manual"/"plugin") pending row on a Fiskil-linked account has no
    // provider replacement, so deleting it here loses it (and its source row) permanently (#5385).
    const pendingTransactions = storeAccountTransactions.filter(
      (t) =>
        t.origin === "fiskil" &&
        t.definition === TransactionStatusEnum.transaction &&
        String(t.status ?? "").toLowerCase() === TransactionStatusEnum.pending,
    );
    const pendingIds = new Set(pendingTransactions.map((t) => t.id).filter((id) => !!id));

    // Delete the pending transactions and let any failure propagate — including a
    // PartialTransactionDeleteError (a surviving partner's repair committed but the delete write
    // failed). Aborting here fails the account sync (both callers wrap this in a try/catch that
    // terminates the account), so we neither delete the still-present pending rows' source rows nor
    // build existingIds from a stale snapshot. Retried next sync.
    if (pendingTransactions.length > 0) {
      await this.transactionStore.transaction.deleteFromVault(pendingTransactions);
    }

    // Delete this account's source rows orphaned by the delete — sourceId no longer maps to a live
    // transaction. The live set excludes the pending rows just removed, so their sources look orphaned
    // (correct), while posted, CBAT and non-pending rows stay live. Not gated on the pending views still
    // existing, so a retry after a partial failure still cleans stranded source rows (mirrors the Plaid
    // item-8 fix, #4504).
    const liveIds = new Set(
      storeAccountTransactions
        .filter((t) => !pendingIds.has(t.id))
        .map((t) => t.id)
        .filter((id) => !!id),
    );
    const sourceTransactions = (
      this.importStore.sourceTransaction.sourceTransactionViews() ?? []
    ).filter((t) => t.accountId === account.id && !liveIds.has(t.sourceId));
    if (sourceTransactions.length > 0) {
      await this.importStore.sourceTransaction.deleteFromVault(sourceTransactions);
    }

    // existingIds and the non-pending window date reflect what's actually in the vault: this account,
    // real transactions only, excluding every pending row (deleted above). Exclude pending by `status`
    // so a still-stored posted row the provider re-returns stays in existingIds and isn't re-inserted as
    // a duplicate. With no store rows and no pending this falls through to `fromDate: undefined` below,
    // matching the pre-existing empty-account behaviour.
    const storeNonPending = storeAccountTransactions.filter(
      (t) =>
        t.definition === TransactionStatusEnum.transaction &&
        String(t.status ?? "").toLowerCase() !== TransactionStatusEnum.pending,
    );

    // get the earliest pending transaction date, so `fromDate` widens far enough back to re-fetch the
    // posted replacement of each deleted pending row (otherwise it could sit before the window).
    const earliestPendingDate = pendingTransactions.reduce((min, t) => {
      const d = t.postDate ?? t.transactionDate;
      if (d) {
        return Math.min(min, d.getTime());
      }
      return min;
    }, Infinity);

    let latest = 0;
    for (const t of storeNonPending) {
      const d = t.postDate ?? t.transactionDate;
      if (d) {
        const ts = d.getTime();
        if (ts > latest) {
          latest = ts;
        }
      }
    }
    // Pending rows we deliberately KEPT — the ones the origin-scoped delete above left in the store
    // (non-Fiskil pending: a manually entered / CSV-imported row, or a Fiskil pending a user edited in
    // the bank form, which rewrites origin to "manual" but leaves the provider transaction_id as the
    // row id). `storeNonPending` excludes them by status, so without this they'd be absent from
    // existingIds — and because the row id IS the Fiskil transaction_id (fiskil.action.transaction.ts),
    // the provider re-returning that id would import a SECOND cipher (built vid:"" → commitCreate)
    // alongside the kept row (#5385). Add them to existingIds so a returning provider row dedups against
    // the kept one instead. A genuinely manual/CSV pending has an id Fiskil never returns, so this is a
    // no-op for it; only the edited-Fiskil-pending case is actually deduped.
    const keptPendingIds = storeAccountTransactions
      .filter(
        (t) =>
          t.definition === TransactionStatusEnum.transaction &&
          String(t.status ?? "").toLowerCase() === TransactionStatusEnum.pending &&
          !pendingIds.has(t.id),
      )
      .map((t) => t.id)
      .filter((id) => !!id);
    const existingIds = new Set<string>([
      ...storeNonPending.map((t) => t.id).filter((id) => !!id),
      ...keptPendingIds,
    ]);

    // return the earliest pending transaction date or the latest non-pending date
    if (earliestPendingDate !== Infinity && (latest === 0 || earliestPendingDate < latest)) {
      const d = new Date(earliestPendingDate);
      return {
        fromDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
        existingIds,
      };
    }

    if (latest === 0) {
      return { fromDate: undefined, existingIds };
    }
    const date = new Date(latest);
    return {
      fromDate: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
      existingIds,
    };
  }

  /**
   * Builds main + source transaction rows and links `transaction.sourceLink.id` to the source row
   * (same pattern as Basiq: save source first, then transactions).
   */
  private buildFiskilTransactionsWithSourceRows(
    account: AccountView,
    fiskilTransactions: FiskilTransaction[],
    transactionCategoryMap: Map<string, CategoryView[]>,
  ): { transactionViews: TransactionView[]; sourceTransactionViews: SourceTransactionsView[] } {
    const transactionViews: TransactionView[] = [];
    const sourceTransactionViews: SourceTransactionsView[] = [];

    for (const tx of fiskilTransactions) {
      const categoryViews = transactionCategoryMap.get(tx.transaction_id);
      const transactionView = this.fiskilTransactionAction.toTransactionView(
        tx,
        account,
        categoryViews,
      );
      const categoryNames = this.getFiskilCategoryNamesForTransaction(tx, categoryViews);
      const sourceView = this.fiskilTransactionAction.toSourceTransactionView(
        account,
        transactionView,
        categoryNames,
        tx,
      );
      transactionView.sourceLink.id = sourceView.id;
      transactionViews.push(transactionView);
      sourceTransactionViews.push(sourceView);
    }

    return { transactionViews, sourceTransactionViews };
  }

  private getFiskilCategoryNamesForTransaction(
    tx: FiskilTransaction,
    categoryViews?: CategoryView[],
  ): string[] {
    if (categoryViews?.length) {
      return categoryViews.map((c) => c.name);
    }
    const cat = tx.category;
    if (!cat) {
      return [];
    }
    const name = cat.secondary_category ?? cat.primary_category;
    return name ? [name] : [];
  }

  /**
   * Maps Fiskil transaction categories to CategoryViews.
   * Resolves existing categories from vault, creates new ones for the batch, deduplicates by name.
   * @returns transactionCategoryMap (tx_id -> CategoryView[]) and categoriesToSave (new categories only)
   */
  private mapFiskilCategoriesToViews(fiskilTransactions: FiskilTransaction[]): {
    transactionCategoryMap: Map<string, CategoryView[]>;
    categoriesToSave: CategoryView[];
  } {
    const transactionCategoryMap = new Map<string, CategoryView[]>();
    const categoryByName = new Map<string, CategoryView>();
    const categoriesToSave: CategoryView[] = [];
    const existingCategories = this.userStore.categories.categoryViews();

    const getOrCreateCategory = (name: string): CategoryView => {
      const key = name.toLowerCase();
      const existing = categoryByName.get(key);
      if (existing) {
        return existing;
      }
      const inVault = existingCategories.find((c) => c.name.toLowerCase() === key);
      if (inVault) {
        categoryByName.set(key, inVault);
        return inVault;
      }
      const newCategory = new CategoryView({
        dc: new Date().toISOString(),
        dm: new Date().toISOString(),
        gd: false,
        sc: "fiskil",
        id: crypto.randomUUID(),
        n: name,
        v: 2,
        vid: null,
      });
      categoryByName.set(key, newCategory);
      categoriesToSave.push(newCategory);
      return newCategory;
    };

    const getCategoryName = (tx: FiskilTransaction): string | undefined => {
      const cat = tx.category;
      if (!cat) {
        return undefined;
      }
      return cat.secondary_category ?? cat.primary_category;
    };

    for (const tx of fiskilTransactions) {
      const name = getCategoryName(tx);
      const categories: CategoryView[] = name ? [getOrCreateCategory(name)] : [];
      transactionCategoryMap.set(tx.transaction_id, categories);
    }

    return { transactionCategoryMap, categoriesToSave };
  }
}
